import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/AppLayout";
import { Copy, Ban, CheckCircle2, Search, X, Users } from "lucide-react";

const DAY = 86400000;
const segmentOf = (h, now) => {
  if (h.status === "blocked") return "blocked";
  if (h.reward_status === "available") return "reward";
  const last = h.last_activity_at ? new Date(h.last_activity_at).getTime() : 0;
  if ((h.visits || 0) >= 5) return "regular";
  if (last && now - last > 30 * DAY) return "at_risk";
  return "active";
};
const SEGMENTS = [["all", "All"], ["regular", "Regulars"], ["reward", "Reward ready"], ["at_risk", "At risk"], ["blocked", "Blocked"]];
const ago = (iso) => {
  if (!iso) return "—";
  const d = Math.floor((Date.now() - new Date(iso).getTime()) / DAY);
  return d === 0 ? "Today" : d === 1 ? "Yesterday" : d < 30 ? `${d}d ago` : `${Math.floor(d / 30)}mo ago`;
};

function Badge({ seg }) {
  const map = { regular: ["Regular", "bg-[#0B0D10] text-[#C8FF4D]"], reward: ["Reward ready", "bg-[#C8FF4D] text-[#0B0D10]"], at_risk: ["At risk", "bg-amber-100 text-amber-900"], blocked: ["Blocked", "bg-red-50 text-red-700"], active: ["Active", "bg-[#F4F1E9] text-[#0B0D10]/70"] };
  const [label, cls] = map[seg] || map.active;
  return <span className={`inline-block font-mono text-[10px] uppercase tracking-[0.12em] px-2 py-0.5 rounded-full ${cls}`}>{label}</span>;
}

function DetailDrawer({ detail, onClose, onBlock, onCopy }) {
  if (!detail) return null;
  const h = detail.holder;
  return (
    <div className="fixed inset-0 z-[100] bg-[#0B0D10]/50 backdrop-blur-sm flex justify-end" onClick={onClose}>
      <div className="h-full w-full max-w-md bg-[#F4F1E9] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="customer-detail">
        <div className="bg-[#0B0D10] text-white px-6 py-5 flex items-center justify-between sticky top-0">
          <div>
            <div className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-[#C8FF4D]">Pass</div>
            <div className="font-display font-extrabold text-[22px] tracking-tight">{h.pass_number}</div>
          </div>
          <button onClick={onClose} className="h-9 w-9 rounded-full grid place-items-center bg-white/[0.08] hover:bg-white/[0.15]" data-testid="customer-detail-close"><X size={15} /></button>
        </div>
        <div className="p-6 space-y-5">
          <div className="grid grid-cols-3 gap-3">
            {[["Balance", h.balance], ["Visits", h.visits], ["Redeemed", h.rewards_redeemed || 0]].map(([l, v]) => (
              <div key={l} className="rounded-2xl bg-white border border-[#0B0D10]/10 p-4"><div className="font-mono text-[10px] uppercase tracking-[0.16em] text-[#0B0D10]/50">{l}</div><div className="font-display font-extrabold text-[24px] mt-1 tabular-nums">{v}</div></div>
            ))}
          </div>
          <div className="text-[13px] text-[#0B0D10]/60" data-testid="customer-wallet-line">{h.wallet_type} wallet · {h.wallet_devices === null || h.wallet_devices === undefined ? "" : h.wallet_devices > 0 ? <span className="text-[#3a7d2c] font-semibold">on {h.wallet_devices === 1 ? "their phone" : `${h.wallet_devices} phones`}</span> : <span className="text-[#C2410C] font-semibold">not on a phone yet — can't receive updates</span>}{h.wallet_devices !== null && h.wallet_devices !== undefined ? " · " : ""}joined {new Date(h.issued_at).toLocaleDateString("en-AU")} · last seen {ago(h.last_activity_at)}</div>
          <div className="flex gap-2">
            <button onClick={() => onCopy(h.barcode_token)} className="rounded-full border border-[#0B0D10]/15 hover:border-[#0B0D10] h-9 px-4 text-[12.5px] font-semibold inline-flex items-center gap-1.5"><Copy size={13} />Copy pass token</button>
            <button onClick={() => onBlock(h)} data-testid="block-customer" className={`rounded-full h-9 px-4 text-[12.5px] font-semibold inline-flex items-center gap-1.5 ${h.status === "blocked" ? "bg-[#C8FF4D] text-[#0B0D10]" : "border border-red-200 text-red-700 hover:bg-red-50"}`}>
              {h.status === "blocked" ? <><CheckCircle2 size={13} />Restore</> : <><Ban size={13} />Block pass</>}
            </button>
          </div>
          <div>
            <div className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-[#0B0D10]/50 mb-2">History</div>
            <ul className="rounded-2xl bg-white border border-[#0B0D10]/10 divide-y divide-[#0B0D10]/[0.06]">
              {detail.transactions.filter((t) => t.type !== "pending").map((t) => (
                <li key={t.id} className="flex justify-between items-center px-4 py-2.5 text-[12.5px]">
                  <span className="capitalize font-medium">{t.type.replace(/_/g, " ")}{t.change ? <span className="text-[#0B0D10]/45 ml-1.5 tabular-nums">{t.change > 0 ? `+${t.change}` : t.change}</span> : null}</span>
                  <span className="text-[#0B0D10]/45 tabular-nums">{new Date(t.created_at).toLocaleString("en-AU", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Customers() {
  const { activeOrg } = useAuth();
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState(null);
  const load = () => activeOrg && api.get(`/orgs/${activeOrg.id}/customers`).then((r) => setRows(r.data));
  useEffect(() => { load(); }, [activeOrg]);
  const now = Date.now();
  const tagged = useMemo(() => rows.map((h) => ({ ...h, seg: segmentOf(h, now) })), [rows]);
  const counts = useMemo(() => tagged.reduce((m, h) => ({ ...m, [h.seg]: (m[h.seg] || 0) + 1 }), {}), [tagged]);
  const filtered = tagged.filter((h) => (filter === "all" || h.seg === filter) && (!q || (h.pass_number || "").toLowerCase().includes(q.toLowerCase())));
  if (!activeOrg) return null;

  const open = async (h) => { const { data } = await api.get(`/orgs/${activeOrg.id}/customers/${h.id}`); setDetail(data); };
  const block = async (h) => { await api.post(`/orgs/${activeOrg.id}/customers/${h.id}/block`, { block: h.status !== "blocked" }); toast.success(h.status === "blocked" ? "Pass restored" : "Pass blocked"); load(); setDetail(null); };
  const copyToken = async (t) => { try { await navigator.clipboard.writeText(t); toast.success("Pass token copied"); } catch (_) { toast.error("Couldn't copy — long-press to select it instead"); } };

  return (
    <div>
      <PageHeader title="Customers" subtitle={`${rows.length} pass holders · anonymous by design, no personal data stored`} />
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#0B0D10]/40" />
          <input data-testid="customer-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search pass number…"
            className="w-full h-10 pl-9 pr-3 rounded-full bg-white border border-[#0B0D10]/10 focus:border-[#0B0D10] focus:outline-none text-[13.5px]" />
        </div>
        <div className="flex items-center gap-1 rounded-full bg-[#EDE9DF] border border-[#0B0D10]/[0.06] p-1 overflow-x-auto">
          {SEGMENTS.map(([k, l]) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`filter-${k}`} className={`px-3 h-8 rounded-full text-[12px] font-medium whitespace-nowrap transition-colors ${filter === k ? "bg-white text-[#0B0D10] shadow-sm" : "text-[#0B0D10]/55 hover:text-[#0B0D10]"}`}>
              {l}{k !== "all" && counts[k] ? <span className="ml-1.5 tabular-nums text-[#0B0D10]/45">{counts[k]}</span> : null}
            </button>
          ))}
        </div>
      </div>
      {rows.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed border-[#0B0D10]/10 p-16 text-center" data-testid="customers-empty">
          <Users size={22} className="mx-auto text-[#0B0D10]/30 mb-3" />
          <h3 className="font-display text-xl font-semibold">Your first regular starts here.</h3>
          <p className="text-[13px] text-[#0B0D10]/50 mt-1">Pass holders appear the moment someone scans your QR.</p>
        </div>
      ) : (
        <div className="rounded-2xl bg-white border border-[#0B0D10]/10 overflow-hidden" data-testid="customers-table">
          <table className="w-full text-[13.5px]">
            <thead className="text-left font-mono text-[10px] uppercase tracking-[0.16em] text-[#0B0D10]/50 border-b border-[#0B0D10]/[0.06]">
              <tr><th className="px-5 py-3">Pass</th><th className="px-4 py-3 hidden md:table-cell">Program</th><th className="px-4 py-3">Progress</th><th className="px-4 py-3 hidden sm:table-cell">Visits</th><th className="px-4 py-3 hidden sm:table-cell">Last seen</th><th className="px-4 py-3">Status</th><th className="px-4 py-3"></th></tr>
            </thead>
            <tbody className="divide-y divide-[#0B0D10]/[0.05]">
              {filtered.map((h) => (
                <tr key={h.id} className="hover:bg-[#F4F1E9]/60 cursor-pointer transition-colors" data-testid={`customer-row-${h.id}`} onClick={() => open(h)}>
                  <td className="px-5 py-3 font-semibold font-mono text-[12.5px]">{h.pass_number}</td>
                  <td className="px-4 py-3 hidden md:table-cell text-[#0B0D10]/70">{h.program_name}</td>
                  <td className="px-4 py-3 tabular-nums font-display font-bold">{h.balance}</td>
                  <td className="px-4 py-3 hidden sm:table-cell tabular-nums text-[#0B0D10]/70">{h.visits}</td>
                  <td className="px-4 py-3 hidden sm:table-cell text-[#0B0D10]/55">{ago(h.last_activity_at)}</td>
                  <td className="px-4 py-3"><Badge seg={h.seg} /></td>
                  <td className="px-4 py-3 text-right"><button onClick={(e) => { e.stopPropagation(); copyToken(h.barcode_token); }} data-testid={`copy-token-${h.id}`} title="Copy pass token" className="h-8 w-8 rounded-full grid place-items-center text-[#0B0D10]/40 hover:text-[#0B0D10] hover:bg-[#F4F1E9]"><Copy size={14} /></button></td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan={7} className="px-5 py-10 text-center text-[13px] text-[#0B0D10]/50">No pass holders match.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      <DetailDrawer detail={detail} onClose={() => setDetail(null)} onBlock={block} onCopy={copyToken} />
    </div>
  );
}
